/**
 * Value-moment signal for push capture (2026-07-25).
 *
 * Why this exists. The SEO-surface push capture revealed on a dwell timer OR a
 * scroll past the hero — an INTERRUPTION. Measured over its first three live
 * days it converted 432 users into ZERO subscribers (100 of them actively
 * dismissed it). Over the same window the sibling prompt shown AFTER a user
 * receives their reading converted 2 of 20 users — ~10%.
 *
 * Same site, same browser APIs, same audience, same notification permission
 * dialog. The only difference is WHEN the ask happens: the 10% prompt is
 * structurally incapable of showing before value is delivered (it requires a
 * `readingId`), while the 0% prompt asks a stranger mid-page.
 *
 * So the ask moves to the value moment. A result surface announces "the seeker
 * just got what they came for" exactly once, and the capture listens instead of
 * running a timer. Deliberately a DOM CustomEvent rather than React context:
 * the emitter (a shared result component, rule 46) and the listener (a lazily
 * mounted, ssr:false slide-in) live in different trees on every surface, and a
 * provider spanning both would mean touching every page shell.
 *
 * Content surfaces with no result moment (panchang, choghadiya, festival hubs)
 * keep the engagement trigger — there is nothing to wait for there.
 */

/** @internal */
export const VALUE_MOMENT_EVENT = "ishvaram:push-value-moment";

export type ValueMomentDetail = {
  /** What produced the value, e.g. "kundali_result" — carried into telemetry. */
  readonly source: string;
};

/**
 * Announce that the seeker just received real value on this page.
 *
 * Safe to call from any client component; a no-op during SSR. Emitters should
 * fire this ONCE per delivered result (the capture itself is fire-once, so a
 * duplicate emit cannot double-count an impression).
 */
export function emitValueMoment(source: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<ValueMomentDetail>(VALUE_MOMENT_EVENT, { detail: { source } })
  );
}

/**
 * Subscribe to the value moment. Returns an unsubscribe function.
 *
 * A late subscriber misses an earlier emit by design: the capture mounts with
 * the page, and a result that already rendered before it mounted is a
 * navigation/restore case, not a fresh value moment worth interrupting.
 */
export function onValueMoment(handler: (detail: ValueMomentDetail) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<ValueMomentDetail>).detail;
    handler(detail ?? { source: "unknown" });
  };
  window.addEventListener(VALUE_MOMENT_EVENT, listener);
  return () => window.removeEventListener(VALUE_MOMENT_EVENT, listener);
}
